/**
 * Shared helpers for the add-influencer → batch-sync → details-public flow.
 */

export function isBatchJobComplete(job) {
  if (!job) return false;
  const status = String(
    job?.status || job?.state || job?.job_status || "",
  ).toUpperCase();
  // e.g. completed_with_issue, COMPLETED_WITH_ISSUE
  if (
    status === "COMPLETED_WITH_ISSUE" ||
    (status.includes("COMPLETED") && status.includes("ISSUE"))
  ) {
    return true;
  }
  return (
    job?.is_complete === true ||
    job?.completed === true ||
    job?.done === true ||
    status === "COMPLETED" ||
    status === "COMPLETE" ||
    status === "SUCCESS" ||
    job?.progress === 100
  );
}

/** True for both clean completions and “completed with issue” — use everywhere the UI should treat a job as done. */
export function isBatchJobSettledSuccessOrWithIssue(job) {
  return isBatchJobComplete(job);
}

/**
 * `is_updated` on a platform object from collection/list APIs: 0 = still syncing; 1 = all fetchable
 * fields are resolved (null/empty means “no data”, not loading); &lt;0 = profile issues.
 * Also reads `isUpdated` if the API uses camelCase.
 * @param {object|undefined|null} platformData
 * @returns {number|undefined}
 */
export function getPlatformIsUpdatedNumberFromObject(platformData) {
  if (!platformData || typeof platformData !== "object") return undefined;
  const raw = platformData.is_updated ?? platformData.isUpdated;
  if (raw === null || raw === undefined || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function normIdentifier(a) {
  return String(a || "")
    .trim()
    .replace(/^@+/, "")
    .toLowerCase();
}

/**
 * Merge handle arrays from the add-influencer response (e.g. brand_new +
 * already_in_db_not_in_collection), preserving first-seen casing, deduped by normIdentifier.
 * @param {...(string[]|undefined|null)} handleArrays
 * @returns {string[]}
 */
export function mergeQueuedInfluencerHandles(...handleArrays) {
  const seen = new Set();
  const out = [];
  (handleArrays || []).forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((h) => {
      const s = String(h || "").trim();
      if (!s) return;
      const key = normIdentifier(s);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(s);
    });
  });
  return out;
}

/**
 * How many influencers **already in the collection** count toward the 200 list cap.
 * Omits in-table placeholders for handles not yet resolved (`_pendingPlaceholder` / `__pending__` ids)
 * so the add-influencer validation matches what the user sees as “saved” rows.
 * @param {Array|undefined} list
 * @returns {number}
 */
export function countExistingInfluencerRowsForAddLimit(list) {
  if (!Array.isArray(list)) return 0;
  const seen = new Set();
  let n = 0;
  for (const row of list) {
    if (!row || row._pendingPlaceholder === true) continue;
    const id = row?.influencer_data?.influencer_id;
    if (!id || String(id).startsWith("__pending__")) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    n += 1;
  }
  return n;
}

export function makePendingInfluencerId(batchId, handle) {
  return `__pending__${String(batchId)}__${String(handle).replace(/\W/g, "_")}`;
}

/**
 * Merges batch /influencer/get-batch-sync-status jobs into placeholder rows (match by identifier_id),
 * and returns which real influencer_ids need details-public fetches.
 *
 * @param {Array} prevList
 * @param {Object} prevInprog
 * @param {Array} jobs
 * @returns {{ list: Array, inprog: Object, detailsToFetch: string[] }}
 */
export function applyBatchSyncJobsToRows(prevList, prevInprog, jobs) {
  const list = [...(prevList || [])];
  const inprog = { ...(prevInprog || {}) };
  const detailsToFetch = [];
  const seen = new Set();

  (jobs || []).forEach((job) => {
    const realId = job?.influencer_id;
    const ident =
      job?.identifier_id ||
      job?.handle ||
      job?.username ||
      job?.social_handle ||
      "";
    if (!realId) return;

    // Queue details-public for any resolved influencer_id while batch sync is in flight
    // (not only when the job is marked complete — profile may still be syncing).
    if (!seen.has(realId)) {
      seen.add(realId);
      detailsToFetch.push(realId);
    }

    if (ident === "" || ident == null) return;
    const idx = list.findIndex(
      (r) =>
        r?._pendingPlaceholder === true &&
        normIdentifier(r?._syncHandle) === normIdentifier(ident),
    );
    if (idx === -1) return;

    const row = list[idx];
    const oldId = row?.influencer_data?.influencer_id;
    const rawPd = row?.platforms_data;
    const pd = Array.isArray(rawPd) ? { ...rawPd[0] } : { ...(rawPd || {}) };
    if (oldId) delete inprog[oldId];
    inprog[realId] = inprog[realId] || {
      name: false,
      profile_pic: false,
      followers: false,
      content_count: false,
      engagement_rate: false,
    };
    const jobPd = job?.platforms_data;
    const jobPd0 = Array.isArray(jobPd) ? jobPd[0] : jobPd;
    const isUpdatedFromJob = job?.is_updated ?? jobPd0?.is_updated;
    list[idx] = {
      ...row,
      _pendingPlaceholder: false,
      is_newly_added: true,
      detail_public_received: false,
      influencer_data: {
        ...row.influencer_data,
        influencer_id: realId,
        name: row.influencer_data?.name || ident,
      },
      platforms_data: {
        ...pd,
        platform_id: job.platform_id || pd.platform_id,
        identifier_id: job.identifier_id || job.handle || ident,
        is_updated:
          isUpdatedFromJob !== undefined && isUpdatedFromJob !== null
            ? isUpdatedFromJob
            : (pd.is_updated !== undefined && pd.is_updated !== null
              ? pd.is_updated
              : 0),
      },
    };
  });

  return {
    list: sortInfluencerRowsForSyncDisplay(list),
    inprog,
    detailsToFetch,
  };
}

function rowIsPlaceholderForSort(row) {
  return row?._pendingPlaceholder === true;
}

function getRowIsUpdatedForSort(row) {
  const pd = row?.platforms_data;
  const p = Array.isArray(pd) ? pd[0] : pd || {};
  const n = getPlatformIsUpdatedNumberFromObject(p);
  return n !== undefined ? n : null;
}

/**
 * Profile has finished initial sync: ok (e.g. is_updated 1) or with issue (-9, -12, -15) — not still at 0.
 * “Completed” and “completed-with-issue” are both settled for ordering.
 * @param {Object} row
 * @returns {boolean}
 */
export function isRowProfileSyncSettledForSort(row) {
  if (row?._pendingPlaceholder) return false;
  const n = getRowIsUpdatedForSort(row);
  if (!Number.isFinite(n)) return false;
  return n !== 0;
}

/**
 * Display order for the add-influencer table (top = first in array):
 * 0) Rows with `details-public` merged in this flow (`_addFlowEnrichedAt`) — **newest first**
 * 1) In-progress new rows (real `influencer_id`, `is_updated` 0) still waiting for public details
 * 2) Placeholder rows (handle only, pending id)
 * 3) The rest of the collection — settled before not-settled, then original index
 * @param {Array} list
 * @returns {Array}
 */
function getAddFlowDisplayBucket(row) {
  if (row?._addFlowEnrichedAt != null) return 0;
  if (row?._pendingPlaceholder) return 2;
  if (row?.is_newly_added && !isRowProfileSyncSettledForSort(row)) return 1;
  return 3;
}

export function sortInfluencerRowsForSyncDisplay(list) {
  const arr = [...(list || [])];
  const tagged = arr.map((row, index) => ({ row, index }));
  tagged.sort((a, b) => {
    const aB = getAddFlowDisplayBucket(a.row);
    const bB = getAddFlowDisplayBucket(b.row);
    if (aB !== bB) {
      return aB - bB;
    }
    if (aB === 0 && bB === 0) {
      const at = Number(a.row?._addFlowEnrichedAt) || 0;
      const bt = Number(b.row?._addFlowEnrichedAt) || 0;
      if (at !== bt) {
        return bt - at;
      }
      return a.index - b.index;
    }
    if (aB === 1 && bB === 1) {
      return a.index - b.index;
    }
    if (aB === 2 && bB === 2) {
      const ap = a.row?._placeholderOrderIndex;
      const bp = b.row?._placeholderOrderIndex;
      if (ap != null && bp != null && ap !== bp) {
        return ap - bp;
      }
      return a.index - b.index;
    }
    const aSet = isRowProfileSyncSettledForSort(a.row);
    const bSet = isRowProfileSyncSettledForSort(b.row);
    if (aSet !== bSet) {
      return aSet ? -1 : 1;
    }
    return a.index - b.index;
  });
  return tagged.map((t) => t.row);
}

const ACTIVE_SYNC_STATUS = new Set([
  "ACTIVE",
  "PENDING",
  "QUEUED",
  "RUNNING",
  "IN_PROGRESS",
  "PROCESSING",
  "SYNCING",
  "SYNC",
  // API returns these while the job is still in the queue (not yet finished)
  "PRIORITIZED",
  "PRIORITISED",
  "DELAYED",
  "WAITING",
]);

/**
 * A job is "still in flight" for batch completion — anything else is treated as done
 * (including completed, completed_with_issue, failed, error).
 */
export function isBatchJobStillInFlight(job) {
  if (!job) return false;
  if (isBatchJobComplete(job)) return false;
  const s = String(
    job?.status || job?.state || job?.job_status || "",
  );
  const su = s.toUpperCase();
  if (su && (su.includes("FAIL") || su.includes("ERROR") || su === "TIMEOUT")) {
    return false;
  }
  if (!job.influencer_id) {
    return true; // not resolved yet; still in flight for this job
  }
  if (!su) return false;
  if (ACTIVE_SYNC_STATUS.has(su)) return true;
  if (/(PENDING|ACTIVE|IN_PROGRESS|PROGRESS|PRIORITI[ZS]ED|QUEUE|RUN|WAIT|SYNC(?!ED)|DELAY)/i.test(
    su,
  )) {
    return true;
  }
  return false;
}

/**
 * Add-influencer may return `batch_id` with a millisecond suffix; get-batch-sync-status
 * may expect the base id. Strips a trailing `_${digits>=12}` segment.
 * @param {string} batchId
 * @returns {string}
 */
export function normalizeBatchIdForSyncStatus(batchId) {
  const s = String(batchId || "");
  if (!s) return s;
  return s.replace(/_([0-9]{12,})$/i, "");
}

/**
 * Per-job label for batch progress UI (sync `jobs[]` + `summary` fallbacks).
 * @param {object} job
 * @returns {"completed"|"completed_with_issue"|"prioritised"|"waiting"|"active"|"failed"|"unknown"}
 */
export function getBatchJobDisplayKind(job) {
  if (!job) return "unknown";
  if (isBatchJobComplete(job)) {
    const st = String(
      job?.status || job?.state || job?.job_status || "",
    ).toUpperCase();
    if (st === "COMPLETED_WITH_ISSUE" || (st.includes("COMPLETED") && st.includes("ISSUE"))) {
      return "completed_with_issue";
    }
    return "completed";
  }
  const raw = String(
    job?.status || job?.state || job?.job_status || "",
  );
  const st = raw.toUpperCase();
  if (
    st &&
    (st.includes("FAIL") ||
      st.includes("ERROR") ||
      st === "TIMEOUT" ||
      (st.includes("NOT") && st.includes("FOUND")))
  ) {
    return "failed";
  }
  if (st === "PRIORITISED" || st === "PRIORITIZED") {
    return "prioritised";
  }
  if (/(^|_)PRIORIT(ISED|IZED)/.test(st) || /^PRIORIT(ISED|IZED)$/i.test(raw)) {
    return "prioritised";
  }
  if (st === "WAITING" || st === "DELAYED" || st === "QUEUED" || st === "PENDING") {
    return "waiting";
  }
  if (isBatchJobStillInFlight(job)) {
    if (/(^|[_])PRIORIT(ISED|IZED)/.test(st) || /PRIORITI[ZS]ED/.test(st)) {
      return "prioritised";
    }
    if (/(WAIT|DELAY|QUEUE)/.test(st)) {
      return "waiting";
    }
    return "active";
  }
  return "unknown";
}

export function isBatchSyncFinished(summary, responseRoot) {
  if (responseRoot?.is_complete === true) return true;
  if (String(responseRoot?.state || responseRoot?.sync_state || "").toLowerCase() === "complete") {
    return true;
  }
  const s = summary && typeof summary === "object" ? summary : {};
  if ("active" in s) {
    const a = Number(s.active);
    if (Number.isFinite(a) && a > 0) return false;
  }
  if ("waiting" in s) {
    const w = Number(s.waiting);
    if (Number.isFinite(w) && w > 0) return false;
  }
  if ("pending" in s || "queued" in s) {
    const p = Number(s.pending ?? s.queued);
    if (Number.isFinite(p) && p > 0) return false;
  }
  if ("prioritised" in s || "prioritized" in s) {
    const pr = Number(
      s.prioritised ?? s.prioritized,
    );
    if (Number.isFinite(pr) && pr > 0) return false;
  }
  if ("delayed" in s) {
    const d = Number(s.delayed);
    if (Number.isFinite(d) && d > 0) return false;
  }
  const total = Number(s.total);
  const completed = Number(s.completed);
  const completedWithIssue = Number(
    s.completed_with_issue ?? s.completedWithIssue ?? 0,
  );
  const settled =
    (Number.isFinite(completed) ? completed : 0) +
    (Number.isFinite(completedWithIssue) ? Math.max(0, completedWithIssue) : 0);
  if (
    Number.isFinite(total) &&
    total > 0 &&
    Number.isFinite(settled) &&
    settled >= total
  ) {
    return true;
  }
  const jobs = responseRoot?.jobs;
  if (Array.isArray(jobs) && jobs.length > 0) {
    const allSettled = jobs.every((j) => !isBatchJobStillInFlight(j));
    if (allSettled) return true;
  }
  return false;
}
